/**
 * Form-mode dispatcher (REQ-C-024).
 *
 * Decides whether a backend `form` message should render as a structured
 * InlineForm or be split into a conversational sequence of one
 * question-per-field turns. The decision is purely a function of the
 * resolved widget config + the field count — no business logic, no
 * intent inspection, no DOM dependencies — so it's trivially testable
 * under happy-dom and reusable from anywhere the widget receives a
 * form message.
 *
 * Why a dedicated module:
 *  - Keeps ChatWidget.handleTransportMessage() small (the dispatch
 *    decision is the only branching logic the widget needs to add for
 *    REQ-C-024; the actual rendering is delegated to InlineForm /
 *    MessageList which already exist).
 *  - Operators tuning the threshold (admin slider on
 *    ConfigurableParameter `widget.form_mode_threshold`) get a
 *    deterministic, observable contract: count fields, compare to
 *    threshold, branch.
 */

import type {
  FormMessage,
  FormFieldDefinition,
  ResolvedWidgetConfig,
} from "../types";

/**
 * Disposition for a backend form message. The widget renders a
 * structured InlineForm when "form"; emits a conversational prompt
 * stream (one bot bubble per field) when "conversational".
 */
export type FormDisposition = "form" | "conversational";

/**
 * Decide whether a form payload should render as InlineForm or be
 * split into a conversational sequence based on the resolved config.
 *
 * Threshold rule (mode = "auto"):
 *   form.fields.length >= config.formModeThreshold  → "form"
 *   form.fields.length <  config.formModeThreshold  → "conversational"
 *
 * Explicit mode overrides win:
 *   mode = "form"           → always "form"
 *   mode = "conversational" → always "conversational"
 *
 * Edge cases:
 *   - Zero or negative field count → "conversational" (nothing to
 *     render structurally; let the chat-bubble path show whatever the
 *     bot follows up with).
 */
export function decideFormDisposition(
  form: FormMessage,
  config: ResolvedWidgetConfig,
): FormDisposition {
  const fieldCount = Array.isArray(form.fields) ? form.fields.length : 0;

  if (config.formMode === "form") return "form";
  if (config.formMode === "conversational") return "conversational";

  // mode === "auto"
  if (fieldCount <= 0) return "conversational";
  return fieldCount >= config.formModeThreshold ? "form" : "conversational";
}

/**
 * Translate a multi-field form into a sequence of conversational turns
 * — one bot bubble per field, asking for it in order. The bubble text
 * is the field's label + placeholder hint when available.
 *
 * Used when `decideFormDisposition` returns "conversational" so the
 * widget can emit the existing text-bubble path without spawning the
 * structured InlineForm component. Each turn is independently typed by
 * the user via the normal ChatInput, so backend SHOULD remain prepared
 * to accept individual answers as plain text turns.
 */
export function fieldsToConversationalPrompts(
  fields: FormFieldDefinition[],
): string[] {
  return fields.map((field) => formatFieldPrompt(field));
}

function formatFieldPrompt(field: FormFieldDefinition): string {
  const requiredMarker = field.required ? "" : " (optional)";
  const hint =
    field.placeholder && field.placeholder.length > 0
      ? ` (e.g. ${field.placeholder})`
      : "";
  return `${field.label}${requiredMarker}${hint}`;
}
